import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, TextField, Typography } from '@mui/material';

import { getEnvironment } from '@/utils';
import styles from './styles.module.scss';

/**
 * Device Activation Page — branded entry point for the OAuth device code flow.
 *
 * When a user runs `TSDK.login()` in the Python SDK, they are directed here.
 * This page either:
 *   1. Redirects to Auth0's /activate page (if a user_code is in the URL)
 *   2. Shows a code entry form that redirects to Auth0's /activate page on submit
 */
/** Format a raw code string into XXXX-XXXX form, auto-inserting the hyphen. */
const formatUserCode = (raw: string): string => {
  // Keep only letters and digits
  const clean = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (clean.length <= 4) return clean;
  return `${clean.slice(0, 4)}-${clean.slice(4, 8)}`;
};

export const DeviceActivationPage = () => {
  const [searchParams] = useSearchParams();
  const [userCode, setUserCode] = useState(formatUserCode(searchParams.get('user_code') || ''));
  const [redirecting, setRedirecting] = useState(false);

  // Auth0's /activate page only exists on the raw tenant domain, not on
  // custom domains (e.g. auth.dev.treble.tech). Map environment to the
  // correct Auth0 tenant URL.
  const activateUrl = (() => {
    const env = getEnvironment();
    if (env === 'dev' || env === 'local') return 'https://treble-dev.eu.auth0.com/activate';
    if (env === 'staging') return 'https://treble-staging.jp.auth0.com/activate';
    return 'https://treble-prod.eu.auth0.com/activate';
  })();

  // If user_code is in the URL, redirect to Auth0 immediately
  useEffect(() => {
    const code = searchParams.get('user_code');
    if (code) {
      setRedirecting(true);
      window.location.href = `${activateUrl}?user_code=${encodeURIComponent(code)}`;
    }
  }, [searchParams, activateUrl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userCode.trim()) {
      setRedirecting(true);
      window.location.href = `${activateUrl}?user_code=${encodeURIComponent(userCode.trim())}`;
    }
  };

  if (redirecting) {
    return (
      <div className={styles['device-page']}>
        <div className={styles['device-card']}>
          <Typography variant="h5" className={styles['title']}>
            Redirecting to login...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['device-page']}>
      <div className={styles['device-card']}>
        <img src="/treble-logo.png" alt="Treble" className={styles['logo']} />
        <Typography variant="h4" className={styles['title']}>
          Device Activation
        </Typography>
        <Typography className={styles['subtitle']}>
          Enter the code displayed in your terminal to authenticate the Treble SDK.
        </Typography>

        <form onSubmit={handleSubmit} className={styles['code-form']}>
          <TextField
            value={userCode}
            onChange={(e) => setUserCode(formatUserCode(e.target.value))}
            placeholder="XXXX-XXXX"
            variant="outlined"
            fullWidth
            autoFocus
            inputProps={{
              className: styles['code-input'],
              maxLength: 9, // XXXX-XXXX = 9 chars
              autoComplete: 'off',
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!userCode.trim()}
            className={styles['submit-btn']}>
            Continue
          </Button>
        </form>

        <Typography className={styles['help-text']}>
          Run <code>TSDK.login()</code> in Python to get a device code.
        </Typography>
      </div>
    </div>
  );
};
